import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import { nf } from '@/shared/util/numbers'

/**
 * A send over time — 18 Oct 2026.
 *
 * Sent, opened, clicked and unsubscribed, each in its own small panel on one
 * shared time axis. Four panels rather than four lines: a send is thousands
 * and its unsubscribes a handful, and on one scale the handful is a flat line
 * at zero. Each panel has its own scale, and its title says so with the total.
 *
 * The axis runs from the first message sent to seven days after the last one
 * — long enough for the opens to die down, short enough that they are the
 * story. Read from `skidaCrmEmailSends/{id}/timeline` (`sendTimeline.ts`):
 * live counters plus `backfill`, the history rebuilt for older sends.
 */

type Metric = 'sent' | 'opened' | 'clicked' | 'unsubscribed'
const METRICS: { key: Metric; label: string }[] = [
  { key: 'sent', label: 'Sent' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'unsubscribed', label: 'Unsubscribed' },
]

const HOUR = 3_600_000
const DAY = 24 * HOUR
const TAIL_DAYS = 7

export interface Hour {
  hourMs: number
  sent: number
  opened: number
  clicked: number
  unsubscribed: number
}

async function loadTimeline(sendId: string): Promise<Hour[]> {
  const snap = await getDocs(collection(db, CRM_COLLECTIONS.emailSends, sendId, 'timeline'))
  return snap.docs
    .map((d) => {
      const v = d.data()
      const b = (v.backfill ?? {}) as Partial<Record<Metric, number>>
      const num = (x: unknown) => (typeof x === 'number' ? x : 0)
      return {
        hourMs: num(v.hourMs),
        sent: num(v.sent) + num(b.sent),
        opened: num(v.opened) + num(b.opened),
        clicked: num(v.clicked) + num(b.clicked),
        unsubscribed: num(v.unsubscribed),
      }
    })
    .filter((h) => h.hourMs > 0)
    .sort((a, b) => a.hourMs - b.hourMs)
}

const dayFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Oslo', day: 'numeric', month: 'short' })
const timeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Oslo',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

/** Oslo midnight on or before `ms`. */
function osloDayStart(ms: number): number {
  const h = Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Oslo', hour: '2-digit', hour12: false }).format(ms)) % 24
  const m = new Date(ms).getUTCMinutes()
  return Math.floor((ms - h * HOUR - m * 60_000) / 60_000) * 60_000
}

// Geometry, in viewBox units; the SVG scales to the dialog's width.
const W = 760
const LEFT = 8
const RIGHT = 8
const PANEL_H = 56
const TITLE_H = 22
const GAP = 14
const AXIS_H = 22

export function SendTimeline({ sendId, unsubscribedTotal }: { sendId: string; unsubscribedTotal: number }) {
  const [hours, setHours] = useState<Hour[] | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let live = true
    loadTimeline(sendId)
      .then((h) => live && setHours(h))
      .catch(() => live && setError(true))
    return () => {
      live = false
    }
  }, [sendId])

  if (error) return <p className="empty-note">Could not load the timeline.</p>
  if (hours === null) return <p className="micro">Loading the timeline…</p>
  return <SendTimelineChart hours={hours} unsubscribedTotal={unsubscribedTotal} />
}

/** The chart itself, from hours already loaded. */
export function SendTimelineChart({ hours, unsubscribedTotal }: { hours: Hour[]; unsubscribedTotal: number }) {
  const [hover, setHover] = useState<number | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const model = useMemo(() => {
    const sentHours = hours.filter((h) => h.sent > 0)
    if (sentHours.length === 0) return null
    const start = sentHours[0]!.hourMs
    const end = sentHours[sentHours.length - 1]!.hourMs + HOUR + TAIL_DAYS * DAY
    const span = end - start
    // Fine enough to show the first evening's opens, coarse enough to read.
    const bin = span <= 10 * DAY ? 2 * HOUR : span <= 30 * DAY ? 6 * HOUR : DAY
    const n = Math.ceil(span / bin)
    const bins = Array.from({ length: n }, (_, i) => ({ at: start + i * bin, sent: 0, opened: 0, clicked: 0, unsubscribed: 0 }))
    for (const h of hours) {
      if (h.hourMs < start || h.hourMs >= end) continue
      const b = bins[Math.floor((h.hourMs - start) / bin)]!
      b.sent += h.sent
      b.opened += h.opened
      b.clicked += h.clicked
      b.unsubscribed += h.unsubscribed
    }
    const totals = Object.fromEntries(METRICS.map((m) => [m.key, bins.reduce((s, b) => s + b[m.key], 0)])) as Record<Metric, number>
    const max = Object.fromEntries(METRICS.map((m) => [m.key, Math.max(1, ...bins.map((b) => b[m.key]))])) as Record<Metric, number>
    const days: number[] = []
    for (let d = osloDayStart(start) + DAY; d < end; d += DAY) days.push(d)
    return { start, end, bin, bins, totals, max, days }
  }, [hours])

  if (!model) return <p className="micro">No timeline for this send: it went out before sends were timed, and the delivery log has nothing for it.</p>

  const { start, end, bin, bins, totals, max, days } = model
  const x = (ms: number) => LEFT + ((ms - start) / (end - start)) * (W - LEFT - RIGHT)
  const bw = Math.max(1, x(start + bin) - x(start) - 1)
  const height = METRICS.length * (TITLE_H + PANEL_H + GAP) + AXIS_H
  const panelTop = (i: number) => i * (TITLE_H + PANEL_H + GAP) + TITLE_H
  const untimed = Math.max(0, unsubscribedTotal - totals.unsubscribed)
  const hb = hover === null ? null : bins[hover]

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    const svg = svgRef.current
    if (!svg) return
    const r = svg.getBoundingClientRect()
    const vx = ((e.clientX - r.left) / r.width) * W
    const i = Math.floor(((vx - LEFT) / (W - LEFT - RIGHT)) * bins.length)
    setHover(i >= 0 && i < bins.length ? i : null)
  }

  return (
    <div className="stl">
      <div className="stl__chart">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${height}`}
          width="100%"
          role="img"
          aria-label={`Sent ${totals.sent}, opened ${totals.opened}, clicked ${totals.clicked}, unsubscribed ${totals.unsubscribed}, from ${dayFmt.format(start)} to ${dayFmt.format(end)}`}
        >
          {/* Day lines across every panel: the shared axis. */}
          {days.map((d) => (
            <line key={d} x1={x(d)} x2={x(d)} y1={TITLE_H - 4} y2={height - AXIS_H} className="stl__grid" />
          ))}
          {METRICS.map((m, i) => {
            const top = panelTop(i)
            const base = top + PANEL_H
            return (
              <g key={m.key}>
                <text x={LEFT} y={top - 8} className="stl__title">
                  {m.label} <tspan className="stl__total">{nf(totals[m.key])}</tspan>
                  <tspan className="stl__scale"> · tallest bar {nf(max[m.key])}</tspan>
                </text>
                <line x1={LEFT} x2={W - RIGHT} y1={base} y2={base} className="stl__base" />
                {bins.map((b, j) => {
                  const v = b[m.key]
                  if (!v) return null
                  const h = Math.max(2, (v / max[m.key]) * PANEL_H)
                  return (
                    <rect
                      key={j}
                      x={x(b.at) + 0.5}
                      y={base - h}
                      width={bw}
                      height={h}
                      rx={Math.min(2, bw / 2)}
                      className={`stl__bar${hover === j ? ' stl__bar--on' : ''}`}
                    />
                  )
                })}
              </g>
            )
          })}
          {days.map((d, i) =>
            // Every day when there is room, every other one when there is not.
            days.length <= 16 || i % 2 === 0 ? (
              <text key={d} x={x(d)} y={height - 6} className="stl__tick" textAnchor="middle">
                {dayFmt.format(d)}
              </text>
            ) : null,
          )}
          {hb && (
            <line
              x1={x(hb.at + bin / 2)}
              x2={x(hb.at + bin / 2)}
              y1={TITLE_H - 4}
              y2={height - AXIS_H}
              className="stl__cross"
            />
          )}
          {/* The hit area: the whole plot, so a thin bar is easy to find. */}
          <rect
            x={LEFT}
            y={0}
            width={W - LEFT - RIGHT}
            height={height - AXIS_H}
            fill="transparent"
            onPointerMove={onMove}
            onPointerLeave={() => setHover(null)}
          />
        </svg>
        {hb && (
          <div
            // Beside the crosshair, on whichever side has room.
            className={`stl__tip${x(hb.at + bin / 2) > W * 0.6 ? ' stl__tip--left' : ''}`}
            style={{ left: `${(x(hb.at + bin / 2) / W) * 100}%` }}
            role="status"
          >
            <div className="stl__tiptime">
              {timeFmt.format(hb.at)} – {timeFmt.format(hb.at + bin).split(', ').pop()}
            </div>
            {METRICS.map((m) => (
              <div key={m.key} className="stl__tiprow">
                <span>{m.label}</span>
                <strong>{nf(hb[m.key])}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="micro stl__note">
        Bars are {bin === DAY ? 'days' : `${bin / HOUR}-hour periods`} in Oslo time, until seven days after
        the last message went out. Opens and clicks are first opens and first clicks per message.
        {untimed > 0 &&
          ` ${nf(untimed)} unsubscribe${untimed === 1 ? '' : 's'} from before 18 Oct had no time recorded and ${untimed === 1 ? 'is' : 'are'} not on the chart.`}
      </p>
      <details className="stl__table">
        <summary className="micro">Show as a table, by day</summary>
        <table>
          <thead>
            <tr>
              <th>Day</th>
              {METRICS.map((m) => (
                <th key={m.key}>{m.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {byDay(bins).map((r) => (
              <tr key={r.day}>
                <td>{dayFmt.format(r.day)}</td>
                {METRICS.map((m) => (
                  <td key={m.key}>{nf(r[m.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  )
}

function byDay(bins: { at: number; sent: number; opened: number; clicked: number; unsubscribed: number }[]) {
  const out = new Map<number, Record<Metric, number> & { day: number }>()
  for (const b of bins) {
    const d = osloDayStart(b.at)
    const r = out.get(d) ?? { day: d, sent: 0, opened: 0, clicked: 0, unsubscribed: 0 }
    for (const m of METRICS) r[m.key] += b[m.key]
    out.set(d, r)
  }
  return [...out.values()]
}
